/**
 * @module components/assistant/usePanelResize
 * @description 어시스턴트 패널 리사이즈 composable. 패널이 우하단 고정(fixed)이므로
 * 좌측/상단/좌상단 핸들을 바깥쪽으로 끌면 커지는 방식으로 크기를 조절한다.
 * 조절한 크기는 localStorage에 저장해 새로고침 후에도 유지한다(UI 선호 — 대화 데이터 아님).
 * 핸들 더블클릭 시 기본 크기(CSS 브레이크포인트별 기본값)로 복귀한다.
 */
import { ref, computed } from 'vue'

const STORAGE_KEY = 'assistant.panelSize'
const MIN_W = 300
const MIN_H = 380
// 최대 크기는 JS가 아니라 패널 CSS(max-width: calc(100vw - 32px) / max-height: calc(100vh - 48px))가
// 뷰포트 기준으로 클램프한다 — JS 뷰포트 직접 판독 금지(SURINP-302 lint:viewport 게이트) 준수.
// 저장값이 뷰포트보다 커도 표시가 클램프되고, 다음 드래그가 실제 크기(offsetWidth) 기준으로 재시작해 자가 보정된다.

/**
 * 패널 크기 조절 상태/핸들러를 제공한다.
 * @returns {{sizeStyle:import('vue').ComputedRef<object>, startResize:Function, onResizeMove:Function, endResize:Function, resetSize:Function}}
 */
export function usePanelResize() {
  /** 사용자 지정 크기(px) — null이면 CSS 기본값 사용 */
  const width = ref(null)
  const height = ref(null)

  // 진행 중 드래그 상태(반응성 불필요) — { mode, x, y, w, h, moved }
  let drag = null

  // 저장된 크기 복원 — 손상 데이터 무시, 최소값만 보정(최대는 CSS가 클램프)
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY))
    if (saved && Number.isFinite(saved.w) && Number.isFinite(saved.h)) {
      width.value = Math.max(saved.w, MIN_W)
      height.value = Math.max(saved.h, MIN_H)
    }
  } catch (e) { /* 파싱 실패 시 기본 크기 사용 */ }

  /** 인라인 스타일 — 사용자 지정 크기가 없으면 빈 객체(CSS 기본값 유지) */
  const sizeStyle = computed(() => (
    width.value == null ? {} : { width: width.value + 'px', height: height.value + 'px' }
  ))

  /**
   * 핸들 pointerdown — 드래그 시작. 포인터 캡처로 이후 move/up이 핸들로 전달된다.
   * @param {PointerEvent} e
   * @param {'l'|'t'|'tl'} mode - 좌측/상단/좌상단 핸들 구분
   */
  function startResize(e, mode) {
    // 진행 중 드래그가 있으면 두 번째 포인터(멀티터치)는 무시 — 단일 drag 슬롯 보호
    if (drag || e.button !== 0) return
    const panel = e.currentTarget.closest('.aw-panel')
    if (!panel) return
    drag = {
      mode,
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      w: panel.offsetWidth,
      h: panel.offsetHeight,
      moved: false
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (err) { /* 포인터가 이미 해제된 경우 등 — 캡처 없이도 핸들 위 드래그는 동작 */ }
    e.preventDefault()
  }

  /**
   * 핸들 pointermove — 앵커(우하단) 반대 방향으로 끈 거리만큼 확대/축소.
   * @param {PointerEvent} e
   */
  function onResizeMove(e) {
    if (!drag || e.pointerId !== drag.pointerId) return
    // 포인터 캡처 실패로 pointerup을 놓친 stale 드래그 방어 — 버튼이 떨어져 있으면 종료
    if (e.buttons !== undefined && (e.buttons & 1) === 0) {
      endResize(e)
      return
    }
    // 실제 이동이 시작될 때 두 축 모두 확정 — 이동 없는 단순 클릭은 크기를 고정/저장하지 않는다
    drag.moved = true
    width.value = drag.mode === 't' ? drag.w : Math.max(drag.w + (drag.x - e.clientX), MIN_W)
    height.value = drag.mode === 'l' ? drag.h : Math.max(drag.h + (drag.y - e.clientY), MIN_H)
  }

  /** 핸들 pointerup/pointercancel — 드래그를 시작한 포인터의, 실제 이동이 있었던 경우만 저장 */
  function endResize(e) {
    if (!drag) return
    if (e && e.pointerId !== undefined && e.pointerId !== drag.pointerId) return
    const moved = drag.moved
    drag = null
    if (!moved) return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ w: width.value, h: height.value }))
    } catch (err) { /* 저장 실패는 무시 — 이번 세션 크기는 유지됨 */ }
  }

  /** 핸들 더블클릭 — 기본 크기 복귀 */
  function resetSize() {
    drag = null
    width.value = null
    height.value = null
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (e) { /* 무시 */ }
  }

  return { sizeStyle, startResize, onResizeMove, endResize, resetSize }
}
